import * as THREE from "three";

export const THICKNESS = 0.6;
export const Y = 7;
export const R_OUTER = 0;
export const R_INNER = 3;
export const RAIL_HEIGHT = 1.2;
export const hw = 15;
export const hl = 20;

export function filletStart(
	prev: [number, number],
	curr: [number, number],
	next: [number, number],
	r: number,
): { a: [number, number]; b: [number, number] } {
	const len1 = Math.sqrt((curr[0] - prev[0]) ** 2 + (curr[1] - prev[1]) ** 2);
	const len2 = Math.sqrt((next[0] - curr[0]) ** 2 + (next[1] - curr[1]) ** 2);
	const r1 = Math.min(r, len1 * 0.49);
	const r2 = Math.min(r, len2 * 0.49);
	return {
		a: [
			curr[0] - ((curr[0] - prev[0]) * r1) / len1,
			curr[1] - ((curr[1] - prev[1]) * r1) / len1,
		] as [number, number],
		b: [
			curr[0] + ((next[0] - curr[0]) * r2) / len2,
			curr[1] + ((next[1] - curr[1]) * r2) / len2,
		] as [number, number],
	};
}

export function buildUshape(): THREE.Shape {
	const corners: [number, number][] = [
		[-hw, 12],
		[-hw, -hl],
		[hw, -hl],
		[hw, 12],
		[8, 12],
		[8, 2],
		[-8, 2],
		[-8, 12],
	];
	const radii = [R_OUTER, R_OUTER, R_OUTER, R_OUTER, R_INNER, R_INNER, R_INNER, R_INNER];

	const aList: [number, number][] = [];
	const bList: [number, number][] = [];

	for (let i = 0; i < corners.length; i++) {
		const prev = corners.at((i - 1 + corners.length) % corners.length)!;
		const curr = corners[i];
		const next = corners[(i + 1) % corners.length];
		const { a, b } = filletStart(prev, curr, next, radii[i]);
		aList.push(a);
		bList.push(b);
	}

	const shape = new THREE.Shape();
	shape.moveTo(aList[0][0], aList[0][1]);
	shape.quadraticCurveTo(corners[0][0], corners[0][1], bList[0][0], bList[0][1]);
	for (let i = 1; i < corners.length; i++) {
		shape.lineTo(aList[i][0], aList[i][1]);
		shape.quadraticCurveTo(corners[i][0], corners[i][1], bList[i][0], bList[i][1]);
	}
	shape.closePath();
	return shape;
}

export function buildMezzanineShape(): THREE.Shape {
	const shape = buildUshape();

	return shape;
}

export function buildEdgeGeoFromShape(shape: THREE.Shape): THREE.BufferGeometry {
	const contour = shape.getPoints(12);
	const holes = shape.holes.map(h => h.getPoints(12));
	const d = THICKNESS;

	const positions: number[] = [];
	const indices: number[] = [];

	function addOutline(pts: THREE.Vector2[]) {
		for (let i = 0; i < pts.length; i++) {
			const j = (i + 1) % pts.length;
			const x1 = pts[i].x, y1 = pts[i].y;
			const x2 = pts[j].x, y2 = pts[j].y;
			const base = positions.length / 3;
			positions.push(x1, y1, 0, x2, y2, 0, x1, y1, d, x2, y2, d);
			indices.push(base, base + 1, base + 2);
			indices.push(base + 1, base + 3, base + 2);
		}
	}

	addOutline(contour);
	for (const hole of holes) addOutline(hole);

	const g = new THREE.BufferGeometry();
	g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
	g.setIndex(indices);
	g.computeVertexNormals();
	return g;
}

export function computeRailPoints(): THREE.Vector3[] {
	const corners: [number, number][] = [
		[-hw, 12],
		[-hw, -hl],
		[hw, -hl],
		[hw, 12],
		[8, 12],
		[8, 2],
		[-8, 2],
		[-8, 12],
	];
	const radii = [R_OUTER, R_OUTER, R_OUTER, R_OUTER, R_INNER, R_INNER, R_INNER, R_INNER];

	const aList: [number, number][] = [];
	const bList: [number, number][] = [];
	for (let i = 0; i < corners.length; i++) {
		const prev = corners.at((i - 1 + corners.length) % corners.length)!;
		const curr = corners[i];
		const next = corners[(i + 1) % corners.length];
		const { a, b } = filletStart(prev, curr, next, radii[i]);
		aList.push(a);
		bList.push(b);
	}

	const pts: [number, number][] = [];

	const addSegment = (from: [number, number], to: [number, number], n: number) => {
		for (let j = 1; j < n; j++) {
			const t = j / n;
			pts.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
		}
	};

	const addCurve = (cp: [number, number], to: [number, number], n: number) => {
		const from = pts[pts.length - 1];
		for (let j = 1; j < n; j++) {
			const t = j / n;
			const mt = 1 - t;
			const x = mt * mt * from[0] + 2 * mt * t * cp[0] + t * t * to[0];
			const y = mt * mt * from[1] + 2 * mt * t * cp[1] + t * t * to[1];
			pts.push([x, y]);
		}
	};

	pts.push(bList[3]);
	addSegment(bList[3], aList[4], 4);
	pts.push(aList[4]);

	addCurve(corners[4], bList[4], 8);
	pts.push(bList[4]);

	addSegment(bList[4], aList[5], 6);
	pts.push(aList[5]);

	addCurve(corners[5], bList[5], 8);
	pts.push(bList[5]);

	addSegment(bList[5], aList[6], 6);
	pts.push(aList[6]);

	addCurve(corners[6], bList[6], 8);
	pts.push(bList[6]);

	addSegment(bList[6], aList[7], 6);
	pts.push(aList[7]);

	addCurve(corners[7], bList[7], 8);
	pts.push(bList[7]);

	addSegment(bList[7], aList[0], 4);
	pts.push(aList[0]);

	return pts.map((p) => new THREE.Vector3(p[0], Y + THICKNESS, p[1]));
}

export function computeFullPerimeter(): THREE.Vector3[] {
	const corners: [number, number][] = [
		[-hw, 12], [-hw, -hl], [hw, -hl], [hw, 12],
		[8, 12], [8, 2], [-8, 2], [-8, 12],
	];
	const radii = [R_OUTER, R_OUTER, R_OUTER, R_OUTER, R_INNER, R_INNER, R_INNER, R_INNER];

	const aList: [number, number][] = [];
	const bList: [number, number][] = [];
	for (let i = 0; i < corners.length; i++) {
		const prev = corners.at((i - 1 + corners.length) % corners.length)!;
		const curr = corners[i];
		const next = corners[(i + 1) % corners.length];
		const { a, b } = filletStart(prev, curr, next, radii[i]);
		aList.push(a);
		bList.push(b);
	}

	const addSegment = (from: [number, number], to: [number, number], n: number, pts: [number, number][]) => {
		for (let j = 1; j < n; j++) {
			const t = j / n;
			pts.push([from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t]);
		}
	};

	const addCurve = (cp: [number, number], to: [number, number], n: number, pts: [number, number][]) => {
		const from = pts[pts.length - 1];
		for (let j = 1; j < n; j++) {
			const t = j / n;
			const mt = 1 - t;
			pts.push([mt * mt * from[0] + 2 * mt * t * cp[0] + t * t * to[0], mt * mt * from[1] + 2 * mt * t * cp[1] + t * t * to[1]]);
		}
	};

	const pts: [number, number][] = [];
	pts.push(bList[7]);
	for (let i = 0; i < corners.length; i++) {
		addSegment(bList[(i + 7) % corners.length], aList[i], 4, pts);
		pts.push(aList[i]);
		addCurve(corners[i], bList[i], 8, pts);
		pts.push(bList[i]);
	}

	return pts.map((p) => new THREE.Vector3(p[0], Y, p[1]));
}
